import React, { useContext, useEffect, useState, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { ConfigContext } from '../Config/ConfigContext';
import { AppBar, Toolbar, Typography } from '@mui/material';
import { WebSocketContext } from './WebSocketContext';
import './style.css';

/**
 * WebSocketProvider component that establishes and manages a WebSocket connection.
 *
 * This component initializes a WebSocket connection based on configuration,
 * handles connection errors and reconnection logic, and provides the WebSocket instance
 * and reconnect function via context to its children. It displays appropriate status bars
 * when connecting or when disconnected.
 *
 * @component
 * @param {Object} props - The properties passed to the component.
 * @param {React.ReactNode} props.children - The child nodes that will have access to the WebSocket context.
 * @returns {JSX.Element} The rendered provider component.
 */
export const WebSocketProvider = (props) => {
	const [loading, setLoading] = useState(true);
	const [isDisconnected, setIsDisconnected] = useState(false);
	const [initialAttempt, setInitialAttempt] = useState(true); // Track the initial connection attempt
	const { config } = useContext(ConfigContext);
	const webSocketRef = useRef(null); // Use a ref to keep track of the WebSocket instance

	/**
	 * Connects a WebSocket instance based on the provided configuration.
	 * Handles the connection lifecycle including error events, open events, message handling, and close events.
	 *
	 * @function
	 */
	const connectWebSocket = useCallback(() => {
		if (
			webSocketRef.current &&
			(webSocketRef.current.readyState === WebSocket.OPEN ||
				webSocketRef.current.readyState === WebSocket.CONNECTING)
		) {
			return; // If there's already an open or connecting WebSocket connection, do nothing
		}

		const ws = new WebSocket(config.webSocketConfig.host, [
			process.env.REACT_APP_APPID,
		]);

		webSocketRef.current = ws; // Assign the WebSocket instance to the ref

		ws.addEventListener("error", (e) => {
			console.log("WebSocket Error:", e);
			setIsDisconnected(true);
			setInitialAttempt(false); // Subsequent attempts after the initial one
			if (webSocketRef.current === ws) {
				webSocketRef.current = null; // Clear the ref before retrying
			}
			setTimeout(() => {
				console.log("Reconnecting WebSocket after error...");
				connectWebSocket();
			}, 5000); // Retry connection after 5 seconds on error
		});

		ws.addEventListener("open", (event) => {
			console.log("WebSocket Connected to server", event);
			setLoading(false);
			setIsDisconnected(false);
			setInitialAttempt(false); // Connection established
		});

		ws.addEventListener("message", (message) => {
			const { eventName, data } = JSON.parse(message.data);
			ws.dispatchEvent(new CustomEvent(eventName, { detail: data }));
			//console.log("Received data", message.data);
		});

		ws.addEventListener("close", (event) => {
			console.log("WebSocket Closed", event);
			setLoading(false);
			setIsDisconnected(true);
			setInitialAttempt(false); // Subsequent attempts after the initial one
			if (webSocketRef.current === ws) {
				webSocketRef.current = null; // Clear the ref before retrying
			}
			setTimeout(() => {
				console.log("Reconnecting WebSocket after close...");
				connectWebSocket();
			}, 5000); // Retry connection after 5 seconds on close
		});

		ws.reply = ({ eventName, data }) => {
			ws.send(
				JSON.stringify({
					eventName,
					data,
					appId: process.env.REACT_APP_APPID,
					origin: window.location.origin,
				})
			);
		};
	}, [config]);

	useEffect(() => {
		connectWebSocket();

		return () => {
			// We do not want to close the WebSocket connection on unmount
			// if (webSocketRef.current && webSocketRef.current.readyState !== WebSocket.CLOSED) {
			//     webSocketRef.current.close();
			//     webSocketRef.current = null; // Clear the ref on cleanup
			// }
		};
	}, [connectWebSocket]);

	return (
		<>
			{initialAttempt && (
				<AppBar position="static" style={{ height: "25px", backgroundColor: "green" }}>
					<Toolbar variant="dense" style={{ minHeight: "25px" }}>
						<Typography variant="caption" color="white">
							Connecting to real-time updates...
						</Typography>
					</Toolbar>
				</AppBar>
			)}
			{!initialAttempt && isDisconnected && (
				<AppBar position="static" style={{ height: "25px", backgroundColor: "red" }}>
					<Toolbar variant="dense" style={{ minHeight: "25px" }}>
						<Typography variant="caption" color="white">
							Real-time updates are disconnected, retrying...
						</Typography>
					</Toolbar>
				</AppBar>
			)}
			{!loading && !isDisconnected && (
				<WebSocketContext.Provider value={{ webSocket: webSocketRef.current, setWebSocket: connectWebSocket }}>
					{props.children}
				</WebSocketContext.Provider>
			)}
		</>
	);
};

WebSocketProvider.propTypes = {
	children: PropTypes.node.isRequired,
};
